import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NEED_CAPTURE_INTRO_ENABLED, NEED_CAPTURE_WIZARD_ENABLED } from '@banyan/commons/consts/settings-keys.const';
import { Setting } from '@banyan/commons/models/admin/settings.model';
import { Norm } from '@banyan/commons/models/capture-needs/norm/norm.model';
import { CategoriesOlfaction } from '@banyan/commons/models/capture-needs/olfaction/categories-olfaction.model';
import { NoteFamily } from '@banyan/commons/models/capture-needs/olfaction/olfactive-notes-family.model';
import { Category } from '@banyan/commons/models/category.model';
import { EndUse } from '@banyan/commons/models/end-use.model';
import { NoteSuggestion } from '@banyan/commons/models/note-suggestion.model';
import { OlfactionConcept } from '@banyan/commons/models/olfaction-concept.model';
import { OlfactionFamily } from '@banyan/commons/models/olfaction-family.model';
import { NormItem, NormItemV2 } from '@banyan/commons/models/request/norm-item.model';
import { OlfactionItem } from '@banyan/commons/models/request/olfaction-item.model';
import { RequestItem } from '@banyan/commons/models/request/request-item.model';
import { Segment } from '@banyan/commons/models/segment.model';
import { SubTargetMarket, TargetMarket } from '@banyan/commons/models/target-market.model';
import {
  CategoryCaptureNeed,
} from '@banyan/features/capture-needs/components/categories-block/categories-block.component';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class CaptureNeedService {
  httpClient = inject(HttpClient);
  destroyRef = inject(DestroyRef);
  needCapture: any = {
    productUse: {
      segments: signal<Segment[]>([]),
      categories: signal<CategoryCaptureNeed[]>([]),
      selectedCategory: signal<CategoryCaptureNeed | undefined>(undefined),
      selectedEndUseCategory: signal<Category | null>(null),
      selectedSegment: signal<Segment | null>(null),
      selectedEndUse: signal<EndUse | null>(null),
      isSegmentChanged: computed(() => !!this.needCapture.productUse.selectedSegment()),
      isEndUseChanged: computed(() => !!this.needCapture.productUse.selectedEndUse()),
    },
    target: {
      targetMarkets: signal<TargetMarket[]>([]),
      selectedAge: signal<SubTargetMarket | null>(null),
      selectedGender: signal<SubTargetMarket | null>(null),
      selectedDistributionChannel: signal<SubTargetMarket | null>(null),
      selectedMarketSegment: signal<SubTargetMarket | null>(null),
      isMarketSegmentChanged: computed(() => !!this.needCapture.target.selectedMarketSegment()),
    },
    fragrance: {
      olfactiveFamilies: signal<OlfactionFamily[]>([]),
      emotions: signal(null),
      colors: signal(null),
      atmospheres: signal(null),
      categoriesEmotions: signal([]),
      notes: signal<NoteFamily[]>([]),
      selectedOlfactions: signal<OlfactionFamily[]>([]),
      selectedOlfactionsV2: signal<OlfactionConcept[]>([]),
      notesSuggestions: signal<NoteSuggestion[]>([]),
      selectedNotesSuggestions: signal<NoteSuggestion[]>([]),
      inspiration1: '',
      inspiration2: '',
      concept: '',
    },
    costAndDosage: {
      dosage: 0,
      comment: '',
      volume: '',
      mandatoryDosage: false,
    },
    estimatedPrice: {
      priceMin: signal<number>(0),
      priceMax: signal<number>(0),
    },
    recap: {
      norms: signal<Norm[]>([]),
      selectedNorms: signal<Norm[]>([]),
      comment: '',
      acceptNorms: signal<boolean>(false),
      hasErrorAcceptNorms: signal<boolean>(false),
      isSubmitSuccess: signal<boolean>(false),
    },
  };
  languageChanged = signal<boolean>(false);
  loadedNotes = signal<boolean>(false);
  errorLoadNotes = signal<boolean>(false);
  private readonly PATH: string = '/need-capture/data';
  volumeChoices = signal<RequestItem[]>([{
    code: '5-20',
    name: '5 - 20 kg',
  },
    {
      code: '25-50',
      name: '25 - 50 kg',
    },
    {
      code: '75+',
      name: '75+ kg',
    }]);
  isIntroduction = signal<boolean>(true);
  isIntroductionEnabled = signal<boolean | null>(null);
  isNoteWizardEnabled = signal<boolean | null>(null);

  constructor() {
    effect(() => {
      if (this.languageChanged()) {
        if (this.needCapture.productUse.segments().length > 0) {
          this.getSegments(true);
        }
        if (this.needCapture.fragrance.olfactiveFamilies().length > 0) {
          this.getOlfactiveFamilies(true);
        }
        if (this.needCapture.recap.norms().length > 0) {
          this.getNorms(true);
        }
        if (this.needCapture.target.targetMarkets().length > 0) {
          this.getTargetMarkets(true);
        }
      }
    });
  }

  resetData() {
    this.needCapture.productUse.selectedSegment.set(this.needCapture.productUse.segments().length > 0 ? this.needCapture.productUse.segments()[0] : null);
    this.needCapture.productUse.selectedCategory.set(this.needCapture.productUse.categories()[0]);
    this.needCapture.productUse.selectedEndUse.set(null);
    this.needCapture.target.selectedAge.set(null);
    this.needCapture.target.selectedGender.set(null);
    this.needCapture.target.selectedDistributionChannel.set(null);
    this.needCapture.target.selectedMarketSegment.set(null);
    this.needCapture.fragrance.selectedOlfactions.set([]);
    this.needCapture.fragrance.selectedOlfactionsV2.set([]);
    this.needCapture.fragrance.selectedNotesSuggestions.set([]);
    this.needCapture.fragrance.notesSuggestions.set([]);
    this.needCapture.fragrance.inspiration1 = '';
    this.needCapture.fragrance.inspiration2 = '';
    this.needCapture.fragrance.concept = '';
    this.needCapture.costAndDosage.dosage = 0;
    this.needCapture.costAndDosage.volume = '';
    this.needCapture.costAndDosage.comment = '';
    this.needCapture.costAndDosage.mandatoryDosage = false;
    this.needCapture.estimatedPrice.priceMin.set(0);
    this.needCapture.estimatedPrice.priceMax.set(0);
    this.needCapture.recap.selectedNorms.set(this.getDefaultNorms());
    this.needCapture.recap.acceptNorms.set(false);
    this.needCapture.recap.hasErrorAcceptNorms.set(false);
    this.needCapture.recap.isSubmitSuccess.set(false);
    this.needCapture.recap.comment = '';
    this.isIntroduction.set(true);
  }

  getSegments(force?: boolean) {
    if (this.needCapture.productUse.segments().length === 0 || force) {
      this.httpClient.get<Segment[]>(`${environment.middlewareUrl}${this.PATH}/segments`)
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        data => {
          this.needCapture.productUse.segments.set(data);
          this.needCapture.productUse.selectedSegment.set(data.length > 0 ? data[0] : null);
          this.needCapture.productUse.categories.set(this.convertToCategoryCaptureNeed(data));
          this.needCapture.productUse.selectedCategory.set(this.needCapture.productUse.categories()[0]);
        },
      );
    }
  }

  getOlfactiveFamilies(force?: boolean) {
    if (this.needCapture.fragrance.olfactiveFamilies().length === 0 || force) {
      this.httpClient.get<OlfactionFamily[]>(
        `${environment.middlewareUrl}${this.PATH}/olfactions`,
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        data => {
          this.needCapture.fragrance.olfactiveFamilies.set(data);
          this.needCapture.fragrance.emotions.set(this.getFamilyCode('emotions'));
          this.needCapture.fragrance.colors.set(this.getFamilyCode('colors'));
          this.needCapture.fragrance.atmospheres.set(this.getFamilyCode('atmospheres'));
          this.needCapture.fragrance.categoriesEmotions.set(this.transformOlfactionFamily(this.needCapture.fragrance.emotions()));
        },
      );
    }
  }

  getNorms(force?: boolean) {
    if (this.needCapture.recap.norms().length === 0 || force) {
      this.httpClient.get<Norm[]>(
        `${environment.middlewareUrl}${this.PATH}/norms`,
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        data => {
          this.needCapture.recap.norms.set(data);
          this.needCapture.recap.selectedNorms.set(this.getDefaultNorms());
        },
      );
    }
  }

  getNotesSuggestions(params: { emotion: number, color: number, atmosphere: number }) {
    this.loadedNotes.set(true);
    this.errorLoadNotes.set(false);
    this.httpClient.get<NoteSuggestion[]>(`${environment.middlewareUrl}/need-capture/notes/suggestions`, { params })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {
        next: data => {
          this.needCapture.fragrance.notesSuggestions.set(data);
          this.loadedNotes.set(false);
        },
        error: () => {
          this.loadedNotes.set(false);
          this.errorLoadNotes.set(true);
        },
      },
    );
  }

  getDefaultNorms() {
    return this.needCapture.recap.norms().filter((elem: Norm) => elem.default);
  }

  transformOlfactionFamily(olfactionFamily: OlfactionFamily | undefined): CategoriesOlfaction[] {
    const groupedOlfactions: Record<string, CategoriesOlfaction> = {};
    olfactionFamily?.olfactions.forEach(olfaction => {
      if (olfaction.category) {
        const { code } = olfaction.category;
        if (!groupedOlfactions[code]) {
          groupedOlfactions[code] = {
            code,
            name: olfaction.category.name,
            icon: olfaction.category.icon,
            olfactions: [],
          };
        }
        groupedOlfactions[code].olfactions.push(olfaction);
      }
    });

    return Object.values(groupedOlfactions);
  }

  getFamilyCode(code: string) {
    return this.needCapture.fragrance.olfactiveFamilies().find((family: OlfactionFamily) => family.code === code);
  }

  getTargetMarkets(force?: boolean) {
    if (this.needCapture.target.targetMarkets().length === 0 || force) {
      this.httpClient.get<TargetMarket[]>(`${environment.middlewareUrl}${this.PATH}/target-markets`)
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        data => {
          this.needCapture.target.targetMarkets.set(data);
          this.setDefaultData();
        },
      );
    }
  }

  getTargetMarketType(code: string) {
    return this.needCapture.target.targetMarkets().find((elem: TargetMarket) => elem.code === code) ?? null;
  }

  getTargetMarketTypeTarget(targetCode: string, targetOption: string) {
    return this.getTargetMarketType(targetCode)?.targetMarkets?.find((elem: SubTargetMarket) => elem.code === targetOption) ?? null;
  }

  getSelectedCategory(segmentCode: string) {
    this.needCapture.productUse.categories().forEach((elem: Segment) => {
      if (elem.code === segmentCode) {
        this.needCapture.productUse.selectedCategory.set(elem);
      }
    });
  }

  initTargetData() {
    this.needCapture.target.selectedAge.set(null);
    this.needCapture.target.selectedGender.set(null);
    this.needCapture.target.selectedDistributionChannel.set(null);
    this.needCapture.target.selectedMarketSegment.set(null);
  }

  setDefaultData() {
    this.needCapture.target.selectedAge.set(this.getTargetMarketTypeTarget('age', 'whole_family'));
    this.needCapture.target.selectedGender.set(this.getTargetMarketTypeTarget('gender', 'unisex'));
  }

  checkTargetMarket() {
    if (['fine_fragrance', 'personal_care'].indexOf(this.needCapture.productUse.selectedSegment()?.code ?? '') > -1) {
      return !this.needCapture.target.selectedAge() ||
        !this.needCapture.target.selectedGender() ||
        !this.needCapture.target.selectedDistributionChannel() ||
        !this.needCapture.target.selectedMarketSegment();
    } else {
      return !this.needCapture.target.selectedDistributionChannel() ||
        !this.needCapture.target.selectedMarketSegment();
    }
  }

  sendRequestCreation() {
    return this.httpClient.post<void>(
      `${environment.middlewareUrl}/request/create`,
      this.buildRequestCreation(),
    );
  }

  buildRequestCreation() {
    const endUseItem = {
      segment: {
        code: this.needCapture.productUse.selectedSegment().code ?? '',
        name: this.needCapture.productUse.selectedSegment().name ?? '',
      },
      category: {
        code: this.needCapture.productUse.selectedCategory().code ?? '',
        name: this.needCapture.productUse.selectedCategory().name ?? '',
      },
      code: this.needCapture.productUse.selectedEndUse().code ?? '',
      name: this.needCapture.productUse.selectedEndUse().name ?? '',
    };

    const olfactionsArray = this.processOlfactions(this.needCapture.fragrance.selectedOlfactions());
    const normsArray = this.processNorms(this.needCapture.recap.selectedNorms());

    const targetMarkets = {
      age: this.needCapture.target.selectedAge()?.code,
      gender: this.needCapture.target.selectedGender()?.code,
      distributionChannel: this.needCapture.target.selectedDistributionChannel().code,
      marketSegment: this.needCapture.target.selectedMarketSegment().code,
    };

    const olfactiveConceptsItem = {
      items: olfactionsArray,
      notesSuggestions: this.needCapture.fragrance.selectedNotesSuggestions(),
      other: this.needCapture.fragrance.concept,
    };

    const normsItem = {
      items: normsArray,
      comment: '',
    };

    const costAndDosageItem = {
      estimatedPrice: null,
      dosage: this.needCapture.costAndDosage.dosage,
      mandatoryDosage: this.needCapture.costAndDosage.mandatoryDosage,
      comment: null,
      estimatedVolume: this.needCapture.costAndDosage.volume,
    };

    const estimatedPrices = {
      minPrice: this.needCapture.estimatedPrice.priceMin() ?? 0,
      maxPrice: this.needCapture.estimatedPrice.priceMax() ?? 0,
    };

    const functionalBenefit = {
      inspiration1: this.needCapture.fragrance.inspiration1,
      inspiration2: this.needCapture.fragrance.inspiration2,
      concept: this.needCapture.fragrance.concept,
    };

    return {
      customer: { role: '', purpose: '', assistance: '', requestName: '' },
      endUses: { items: [endUseItem] },
      targetMarket: { ...targetMarkets },
      functionalBenefits: { ...functionalBenefit },
      olfactiveConcepts: olfactiveConceptsItem,
      norms: normsItem,
      costAndDosage: costAndDosageItem,
      generalConcept: null,
      olfactiveConcept: undefined,
      olfactiveNotes: null,
      targetCountries: [],
      comment: this.needCapture.recap.comment,
      estimatedPrice: estimatedPrices,
      acceptNorms: this.needCapture.recap.acceptNorms(),
    };
  }

  private convertToCategoryCaptureNeed(segments: Segment[] | null): CategoryCaptureNeed[] {
    if (!segments) return [];
    return segments.map(segment => ({
      id: segment.id.toString(),
      translateKey: segment.name,
      name: segment.name,
      code: segment.code,
    }));
  }

  private processOlfactions(olfactions: OlfactionFamily[]): OlfactionItem[] {
    return olfactions.map(selectedFamily => ({
      family: {
        code: selectedFamily?.code ?? '',
        name: selectedFamily?.name ?? '',
      },
      concepts: selectedFamily.olfactions.map(olfaction => ({
        code: olfaction.code ?? '',
        name: olfaction.name ?? '',
      })),
    }));
  }

  private processNorms(norms: Norm[]): NormItem[] {
    return norms.map(norm => ({
      title: norm.title ?? '',
      code: norm.code ?? '',
      description: norm.description ?? '',
    }));
  }

  sendRequestCreationV2() {
    return this.httpClient.post<void>(
      `${environment.middlewareUrl}/v2/requests`,
      this.buildRequestCreationV2(),
    );
  }

  buildRequestCreationV2() {
    const endUseItem = {
      id: this.needCapture.productUse.selectedEndUse().id,
    };

    const olfactionsArray = this.needCapture.fragrance.selectedOlfactionsV2();
    const normsArray = this.processNormsV2(this.needCapture.recap.selectedNorms());

    const targetMarkets = {
      age: {
        id: this.needCapture.target.selectedAge()?.id,
        code: this.needCapture.target.selectedAge()?.code,
      },
      gender: {
        id: this.needCapture.target.selectedGender()?.id,
        code: this.needCapture.target.selectedGender()?.code,
      },
      distributionChannel: {
        id: this.needCapture.target.selectedDistributionChannel().id,
        code: this.needCapture.target.selectedDistributionChannel().code,
      },
      marketSegment: {
        id: this.needCapture.target.selectedMarketSegment().id,
        code: this.needCapture.target.selectedMarketSegment().code,
      },
    };

    const olfactionConceptsItem = {
      items: olfactionsArray,
      notesSuggestions: this.needCapture.fragrance.selectedNotesSuggestions(),
      other: this.needCapture.fragrance.concept,
    };

    const normsItem = {
      items: normsArray,
    };

    const dosage = {
      dosage: this.needCapture.costAndDosage.dosage,
      mandatoryDosage: this.needCapture.costAndDosage.mandatoryDosage,
      comment: null,
    };

    const estimatedPrices = {
      minPrice: this.needCapture.estimatedPrice.priceMin() ?? 0,
      maxPrice: this.needCapture.estimatedPrice.priceMax() ?? 0,
    };

    const functionalBenefit = {
      inspiration1: this.needCapture.fragrance.inspiration1,
      inspiration2: this.needCapture.fragrance.inspiration2,
      noteComment: this.needCapture.fragrance.concept,
    };

    return {
      endUses: { items: [endUseItem] },
      targetMarket: { ...targetMarkets },
      olfactionConcepts: olfactionConceptsItem,
      functionalBenefits: { ...functionalBenefit },
      dosage: { ...dosage },
      norms: normsItem,
      comment: this.needCapture.recap.comment,
      isNormAccepted: this.needCapture.recap.acceptNorms(),
      estimatedPrice: estimatedPrices,
      estimatedVolume: this.needCapture.costAndDosage.volume,
    };
  }

  private processNormsV2(norms: Norm[]): NormItemV2[] {
    return norms.map(norm => ({
      id: norm.id,
    }));
  }

  getSettings(): void {
    const keys = [NEED_CAPTURE_INTRO_ENABLED, NEED_CAPTURE_WIZARD_ENABLED];

    keys.forEach(key => {
      this.httpClient.get<Setting>(`${environment.middlewareUrl}/settings/${key}`)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: Setting) => {
            const boolValue = data.value === 'true';
            if (key === NEED_CAPTURE_INTRO_ENABLED) {
              this.isIntroductionEnabled.set(boolValue);
            } else if (key === NEED_CAPTURE_WIZARD_ENABLED) {
              this.isNoteWizardEnabled.set(boolValue);
            }
          },
          error: () => {
            console.error(`Error loading setting for key: ${key}`);
          },
        });
    });
  }
}
